import { useRef, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  helpdeskAiContentApi,
  isApiError,
  type AiContentCreateBody,
  type HelpdeskAiContentKind,
} from '@shared/api';
import {
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardFooter,
  ConfirmDialog,
  Heading,
  Input,
  Label,
  RequiredMark,
  PageBackLink,
  PopoverSelect,
  Text,
  Textarea,
} from '@shared/ui';
import { aiContentFormOptionsQuery, aiContentKeys } from '../api/queries';
import { errorKey, outcomeUnknown } from '../model/editor';

type Intent = { body: AiContentCreateBody; key: string };

export function AiContentCreate({
  kind,
  scope,
  canCreate,
  onBack,
  onCreated,
}: {
  kind: HelpdeskAiContentKind;
  scope: string;
  canCreate: boolean;
  onBack: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const options = useQuery(aiContentFormOptionsQuery(scope, kind));
  const [draft, setDraft] = useState({ title: '', content: '', type: '' });
  const [invalid, setInvalid] = useState({ title: false, content: false, type: false });
  const [error, setError] = useState<unknown>();
  const [uncertain, setUncertain] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const intent = useRef<Intent | null>(null);
  const saving = useRef(false);
  const acknowledged = useRef(false);
  const allowLeave = useRef(false);
  const form = useRef<HTMLFormElement>(null);
  const dirty = Boolean(draft.title || draft.content || draft.type);
  const mutation = useMutation({
    mutationFn: (request: Intent) => helpdeskAiContentApi.create(kind, request.body, request.key),
    retry: false,
  });
  const blocker = useBlocker({
    shouldBlockFn: () =>
      !allowLeave.current && !acknowledged.current && (dirty || uncertain || saving.current),
    enableBeforeUnload: !acknowledged.current && (dirty || uncertain),
    withResolver: true,
  });
  const denied = !canCreate || (isApiError(error) && [401, 403].includes(error.status));
  const locked = mutation.isPending || uncertain || acknowledged.current;
  const leave = () => {
    allowLeave.current = true;
    onBack();
  };
  async function submit() {
    if (!canCreate || saving.current || acknowledged.current || (denied && !uncertain)) return;
    if (!intent.current) {
      if (draft.type && (!options.data || options.error)) return;
      const fields = {
        title:
          !draft.title.trim() || draft.title.trim().length > 500 || draft.title.includes('\u0000'),
        content:
          !draft.content || draft.content.length > 100000 || draft.content.includes('\u0000'),
        type:
          (kind === 'instructions' && !draft.type) ||
          Boolean(draft.type && !options.data?.types.some((item) => item.value === draft.type)),
      };
      setInvalid(fields);
      if (fields.title || fields.content || fields.type) {
        form.current
          ?.querySelector<HTMLElement>(
            fields.title
              ? '#ai-create-title'
              : fields.type
                ? '#ai-create-type'
                : '#ai-create-content'
          )
          ?.focus();
        return;
      }
      intent.current = {
        body: {
          title: draft.title.trim(),
          content: draft.content,
          ...(draft.type ? { type: draft.type } : {}),
        },
        key: crypto.randomUUID(),
      };
    }
    saving.current = true;
    setError(undefined);
    try {
      const saved = await mutation.mutateAsync(intent.current);
      acknowledged.current = true;
      setUncertain(false);
      void client.invalidateQueries({ queryKey: aiContentKeys.lists(scope, kind) });
      toast.success(t('aiContent.create.created'));
      allowLeave.current = true;
      onCreated(saved.data.id);
    } catch (cause) {
      setError(cause);
      // A rejected replay does not settle the original request's unknown outcome.
      const unknown = uncertain || outcomeUnknown(cause);
      setUncertain(unknown);
      if (!unknown) intent.current = null;
    } finally {
      saving.current = false;
    }
  }
  const fieldErrors = {
    title: invalid.title
      ? !draft.title.trim()
        ? t('common.validation.required')
        : draft.title.trim().length > 500
          ? t('common.validation.maxLength', { max: 500 })
          : t('common.validation.unsupportedCharacters')
      : undefined,
    content: invalid.content
      ? !draft.content
        ? t('common.validation.required')
        : draft.content.length > 100000
          ? t('common.validation.maxLength', { max: 100000 })
          : t('common.validation.unsupportedCharacters')
      : undefined,
    type: invalid.type
      ? t(draft.type ? 'common.validation.choice' : 'common.validation.required')
      : undefined,
  };
  const feedback = uncertain ? 'unknown' : !canCreate ? 'denied' : error ? 'error' : undefined;
  return (
    <section className="flex min-w-0 flex-col gap-4" aria-label={t(`aiContent.create.${kind}`)}>
      <PageBackLink onClick={onBack}>{t(`aiContent.back.${kind}`)}</PageBackLink>
      <Heading as="h1" size="lg">
        {t(`aiContent.create.${kind}`)}
      </Heading>
      <Card>
        <form
          ref={form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <CardContent className="min-w-0 space-y-4 p-4">
            <div className="space-y-1">
              <Label htmlFor="ai-create-title">
                {t('aiContent.fields.title')}
                <RequiredMark />
              </Label>
              <Input
                id="ai-create-title"
                aria-required="true"
                required
                maxLength={500}
                value={draft.title}
                disabled={locked}
                aria-invalid={invalid.title || undefined}
                aria-describedby={invalid.title ? 'ai-create-title-error' : undefined}
                onChange={(event) => {
                  setDraft({ ...draft, title: event.target.value });
                  setInvalid({ ...invalid, title: false });
                }}
              />
              {fieldErrors.title ? (
                <Text id="ai-create-title-error" role="alert" size="sm" color="destructive">
                  {fieldErrors.title}
                </Text>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ai-create-type">
                {t('aiContent.fields.type')}
                {kind === 'instructions' ? <RequiredMark /> : null}
              </Label>
              <PopoverSelect
                id="ai-create-type"
                triggerAriaLabel={t('aiContent.fields.type')}
                value={draft.type}
                required={kind === 'instructions'}
                disabled={locked || (kind === 'instructions' && Boolean(options.error))}
                loading={options.isPending}
                loadingLabel={t('common.loading')}
                emptyText={t('aiContent.create.noTypes')}
                options={[
                  ...(kind === 'prompts' ? [{ value: '', label: t('crm.missingValue') }] : []),
                  ...(options.data?.types ?? []),
                ]}
                aria-invalid={invalid.type || undefined}
                aria-describedby={
                  [
                    invalid.type && 'ai-create-type-error',
                    options.error && 'ai-create-type-options-error',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                onValueChange={(type) => {
                  setDraft({ ...draft, type });
                  setInvalid({ ...invalid, type: false });
                }}
              />
              {fieldErrors.type ? (
                <Text id="ai-create-type-error" role="alert" size="sm" color="destructive">
                  {fieldErrors.type}
                </Text>
              ) : null}
              {options.error ? (
                <Alert id="ai-create-type-options-error" variant="destructive">
                  <AlertTitle>{t(errorKey(options.error))}</AlertTitle>
                  <AlertDescription>
                    <Button
                      type="button"
                      variant="outline"
                      loading={options.isFetching}
                      onClick={() => {
                        void options.refetch();
                      }}
                    >
                      {t('auth.retry')}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ai-create-content">
                {t('aiContent.fields.content')}
                <RequiredMark />
              </Label>
              <Textarea
                id="ai-create-content"
                aria-required="true"
                required
                rows={12}
                maxLength={100000}
                value={draft.content}
                disabled={locked}
                aria-invalid={invalid.content || undefined}
                aria-describedby={invalid.content ? 'ai-create-content-error' : undefined}
                onChange={(event) => {
                  setDraft({ ...draft, content: event.target.value });
                  setInvalid({ ...invalid, content: false });
                }}
              />
              {fieldErrors.content ? (
                <Text id="ai-create-content-error" role="alert" size="sm" color="destructive">
                  {fieldErrors.content}
                </Text>
              ) : null}
            </div>
            {feedback ? (
              <Alert
                id="ai-create-feedback"
                variant={feedback === 'unknown' ? 'default' : 'destructive'}
              >
                <AlertTitle>
                  {t(
                    feedback === 'unknown'
                      ? 'aiContent.save.unknown'
                      : feedback === 'denied'
                        ? 'aiContent.errors.denied'
                        : errorKey(error)
                  )}
                </AlertTitle>
                <AlertDescription className="space-y-3">
                  {feedback === 'unknown' ? (
                    <>
                      <Text>{t('aiContent.save.sameRetry')}</Text>
                      {denied ? <Text>{t('aiContent.errors.denied')}</Text> : null}
                    </>
                  ) : null}
                </AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
          <CardFooter className="flex-wrap justify-end gap-2 p-4 pt-0">
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending || acknowledged.current}
              onClick={() => {
                if (dirty || uncertain) setCancelOpen(true);
                else leave();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={
                mutation.isPending ||
                acknowledged.current ||
                !canCreate ||
                (!uncertain &&
                  (denied ||
                    (Boolean(draft.type) && (options.isPending || Boolean(options.error)))))
              }
            >
              {t(uncertain ? 'aiContent.save.retrySame' : 'common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
      <ConfirmDialog
        open={cancelOpen || blocker.status === 'blocked'}
        title={t('aiContent.unsaved.title')}
        description={t(uncertain ? 'aiContent.unsaved.unknown' : 'aiContent.unsaved.description')}
        confirmLabel={t('aiContent.unsaved.leave')}
        cancelLabel={t('aiContent.unsaved.stay')}
        confirmButtonProps={{
          disabled: mutation.isPending,
        }}
        onOpenChange={(open) => {
          if (!open) {
            setCancelOpen(false);
            if (blocker.status === 'blocked') blocker.reset();
          }
        }}
        onConfirm={() => {
          if (saving.current) return;
          allowLeave.current = true;
          if (blocker.status === 'blocked') blocker.proceed();
          else leave();
        }}
      />
    </section>
  );
}
